import type { GraphQLError } from 'graphql';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import WebPanelLocationQuery, {
	type WebPanelLocationQuery$data,
	type WebPanelLocationQuery$variables,
} from './__generated__/WebPanelLocationQuery.graphql.ts';

type PreloadWebPanelLocationResult = {
	data: WebPanelLocationQuery$data | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
};

export function preloadWebPanelLocation(
	variables: WebPanelLocationQuery$variables,
): Promise<PreloadWebPanelLocationResult> {
	return query<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>({
		query: WebPanelLocationQuery,
		variables,
	});
}

const getUniqueLocations = (locations: string[]) => Array.from(new Set(locations.filter(Boolean)));

/**
 * Preloads web panel locations with the query shape needed for the requested locations.
 *
 * Multi-location calls issue one batched GraphQL operation and then fan out the result into
 * per-location Apollo cache keys. Callers should keep any batching experiment gate at the call site.
 */
export function preloadWebPanelLocations({
	locations,
	contentId = null,
	spaceKey = null,
}: {
	locations: string[];
	contentId?: string | null;
	spaceKey?: string | null;
}): Promise<PreloadWebPanelLocationResult | { data: undefined }> {
	const uniqueLocations = getUniqueLocations(locations);

	if (uniqueLocations.length === 0) {
		return Promise.resolve({ data: undefined });
	}

	if (uniqueLocations.length === 1) {
		return preloadWebPanelLocation({
			location: uniqueLocations[0],
			...(contentId != null ? { contentId } : {}),
			...(spaceKey != null ? { spaceKey } : {}),
		});
	}

	const variables = {
		location: null,
		locations: uniqueLocations,
		...(contentId != null ? { contentId } : {}),
		...(spaceKey != null ? { spaceKey } : {}),
	};

	return query<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>({
		query: WebPanelLocationQuery,
		variables,
	}).then((result) => {
		const { data } = result;

		if (data?.webPanels != null) {
			type WebPanel = NonNullable<(typeof data.webPanels)[number]>;
			const panelsByLocation = new Map<string, WebPanel[]>();

			data.webPanels.forEach((panel) => {
				if (panel?.location) {
					const panels = panelsByLocation.get(panel.location) ?? [];
					panels.push(panel);
					panelsByLocation.set(panel.location, panels);
				}
			});

			uniqueLocations.forEach((location) => {
				writeQueryInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>({
					query: WebPanelLocationQuery,
					variables: {
						location,
						...(contentId != null ? { contentId } : {}),
						...(spaceKey != null ? { spaceKey } : {}),
					},
					data: {
						webPanels: panelsByLocation.get(location) ?? [],
					},
				});
			});
		}

		return result;
	});
}
